import React, { useEffect, useMemo } from "react";
import { debounce } from "debounce";
import { InputBase } from "./InputBase";
const NumberInput = React.forwardRef(({ prefix = "", postfix = "", className, errorClassName, error, errorText = "", min, minErr = "value is too small", max, maxErr = "value is too big", small, icon, placeholder, trailing, style, disabled, allowDecimal = false, decimalDigits = 2, dataCySuffix, ...rest }, ref) => {
    const [internalErr, setInternalErr] = React.useState("");
    const [internalValue, setInternalValue] = React.useState(rest.value?.toString());
    const formattedInternalValue = useMemo(() => {
        if (internalValue) {
            return internalValue;
        }
        return rest.allowUndefined ? "" : "0";
    }, [internalValue, rest.allowUndefined]);
    const errorDesc = errorText ?? internalErr;
    const isError = error || (errorDesc != null && errorDesc.length > 0);
    useEffect(() => {
        if (parseFloat(formattedInternalValue) !== rest.value) {
            setInternalValue(rest.value?.toString() ?? "");
        }
    }, [rest.value, formattedInternalValue]);
    const onChange = React.useCallback((event) => {
        let stripped = event.currentTarget.value
            .replace(prefix, "")
            .replace(postfix, "");
        if (stripped === "" && rest.allowUndefined) {
            return rest.onChange(undefined);
        }
        const [intPart, decimalPart, ..._] = stripped.split(".");
        const int = +intPart;
        if (allowDecimal && decimalPart != null) {
            stripped = `${int}.${decimalPart.substring(0, decimalDigits)}`;
        }
        else {
            stripped = `${int}`;
        }
        const num = +stripped;
        if (max && num > max) {
            return rest.onChange(max);
        }
        else if (min && num < min) {
            return rest.onChange(min);
        }
        else if (!isNaN(num)) {
            rest.onChange(num);
        }
        if (!isNaN(num)) {
            setInternalValue(stripped);
        }
    }, [rest.onChange, prefix, postfix, setInternalValue]);
    const onKeyDown = React.useCallback((event) => {
        rest.onKeyDown && rest.onKeyDown(event);
        const key = event.key; // const {key} = event; ES6+
        const keys = [..."0123456789".split(""), "Backspace", "Delete"];
        const { selectionStart, selectionEnd, value } = event.currentTarget;
        // on key press check if caret is after postfix. if it is - set it before postfix
        if (keys.includes(key) &&
            postfix &&
            selectionStart != null &&
            selectionStart === selectionEnd &&
            selectionStart === value.length) {
            event.currentTarget.selectionStart =
                event.currentTarget.selectionStart - 1;
            event.currentTarget.selectionEnd =
                event.currentTarget.selectionEnd - 1;
        }
    }, [rest.onKeyDown, postfix]);
    const checkSize = (val) => {
        if (min && val < min)
            return setInternalErr(minErr);
        if (max && val > max)
            return setInternalErr(maxErr);
        if (internalErr)
            return setInternalErr("");
    };
    const handleMinMax = React.useCallback(debounce(checkSize, 1000), [
        min,
        minErr,
        max,
        maxErr,
        internalErr,
    ]);
    React.useEffect(() => {
        if (internalValue !== "")
            handleMinMax(+formattedInternalValue);
    }, [internalValue, min, max]);
    return (React.createElement(InputBase, { small: small, isError: isError, dataCySuffix: dataCySuffix, errorClassName: errorClassName, errorText: errorDesc, className: className, style: style, disabled: disabled, ref: ref, placeholder: placeholder, trailing: trailing, icon: icon, value: placeholder && formattedInternalValue === ""
            ? formattedInternalValue
            : `${prefix}${formattedInternalValue}${postfix}`, onChange: onChange, onKeyDown: onKeyDown, type: "text", onBlur: (e) => {
            rest.onBlur?.(e);
            return () => setInternalValue(rest.value?.toString());
        } }));
});
export default NumberInput;
//# sourceMappingURL=NumberInput.js.map